import { useState } from "react";
import { useAuth } from "@/components/AuthContext";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

const AVATAR_URL = "/edgar-avatar-small.jpg";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  // Read invite code from URL on mount
  useState(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("invite");
    if (code) {
      setInviteCode(code);
      setMode("register");
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) {
        setError("Ingresa tu nombre");
        setLoading(false);
        return;
      }
      result = await register(email, password, name, inviteCode || undefined);
    }

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "var(--bg-main)" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-full overflow-hidden ring-4 ring-emerald-500/20 mb-4 shadow-[0_0_40px_rgba(16,185,129,0.15)]">
            <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
          </div>
          <h1
            className="text-2xl font-bold text-zinc-50 tracking-tight"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Edgar IA
          </h1>
          <p className="text-xs text-zinc-500 mt-1 tracking-wider uppercase">Tutor Agente 360</p>
        </div>

        {/* Form */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <h2 className="text-lg font-semibold text-zinc-100 mb-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </h2>
          <p className="text-xs text-zinc-500 mb-5">
            {mode === "login" ? "Accede a tu tutor IA personalizado" : "Únete a la formación de Agente 360"}
          </p>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5 mb-4"
            >
              <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
              <p data-testid="auth-error" className="text-xs text-red-300">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div>
                <label className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1 block">
                  Nombre
                </label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
                />
              </div>
            )}

            <div>
              <label className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1 block">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
              />
            </div>

            <div>
              <label className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1 block">
                Contraseña
              </label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 pr-10 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 text-zinc-950 font-semibold rounded-full py-2.5 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.2)] mt-2"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin mx-auto" />
              ) : mode === "login" ? (
                "Iniciar sesión"
              ) : (
                "Crear cuenta"
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              data-testid="auth-toggle-mode"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-xs text-zinc-500 hover:text-emerald-400 transition-colors"
            >
              {mode === "login"
                ? "¿No tienes cuenta? Regístrate"
                : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
